import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { WifiKioskConfig, WifiKioskNetwork } from "../scenario/types";
import { audio } from "../lib/audio";

/**
 * Radical fix (2026-07-15, after ~10+ rounds of static cqh/cqmin tuning kept
 * re-breaking the moment content amount changed): MEASURE the real rendered
 * height of a stage's content and shrink a single CSS variable (`--wk-fit`)
 * until it provably fits `.wk-screen`'s fixed pixel box. No more guessing a
 * ratio that only holds for one specific window size + one specific number
 * of Wi-Fi networks -- this recomputes on every resize and every stage/data
 * change, so it can never overflow regardless of how many networks the
 * scenario author adds or how long a translated string is.
 *
 * `.wk-fit` (see styles.css) pairs a `width: calc(100% / var(--wk-fit))`
 * with `transform: scale(var(--wk-fit))` so shrinking the multiplier makes
 * text slightly smaller WITHOUT shrinking the used width -- the terminal
 * still fills edge-to-edge even at e.g. 0.85x, instead of the classic
 * "shrunk terminal with empty margins" look a naive `zoom` would produce.
 */
function useFitScale<T extends HTMLElement = HTMLDivElement, C extends HTMLElement = HTMLDivElement>(
  deps: readonly unknown[],
) {
  const containerRef = useRef<T | null>(null);
  const contentRef = useRef<C | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const cs = getComputedStyle(container);
    const available =
      container.clientHeight - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0);
    if (available <= 0) return;

    // Measure the content's INTRINSIC height (ignore the flex-stretch that
    // normally lets short stages vertically-center/fill leftover space --
    // if we measured the stretched box instead, scrollHeight would just
    // report "however tall the box already is" and overflow could never
    // be detected).
    const prevFlex = content.style.flex;
    const prevHeight = content.style.height;
    content.style.flex = "0 0 auto";
    content.style.height = "auto";

    let s = 1;
    for (let i = 0; i < 26; i++) {
      content.style.setProperty("--wk-fit", String(s));
      const naturalHeight = content.scrollHeight;
      if (naturalHeight * s <= available + 1 || s <= 0.5) break;
      s = Math.max(0.5, Math.round((s - 0.025) * 1000) / 1000);
    }

    content.style.flex = prevFlex;
    content.style.height = prevHeight;
  }, []);

  useLayoutEffect(() => {
    // Reset to 1 first so switching from a tall stage to a short one doesn't
    // stay stuck at a stale small scale.
    contentRef.current?.style.setProperty("--wk-fit", "1");
    measure();
    // Re-measure once more after fonts/webfont metrics settle a frame later.
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(container);
    return () => ro.disconnect();
  }, [measure]);

  return { containerRef, contentRef };
}

/**
 * Public Wi-Fi self-service KIOSK terminal (station).
 * Flow: scan list (signal + encryption) -> connect ->
 *   - trusted (open, strongest): official operator guest-pay page (correct
 *     domain, valid padlock, pay as guest) -> safe.
 *   - trap: a look-alike operator page (wrong domain + typos + asks for a card)
 *     or a company-credential captive portal. Proceeding routes to an SSL/cert
 *     interception error (the connection is now MITM'd) -> risky.
 * The learner can always disconnect and pick another network (no dead end).
 */
type Stage = "off" | "list" | "real" | "connected" | "fake" | "creds" | "cert";

/* Bar heights are inline (data-driven, not CSS) so they must be kept in
   sync with `.wk-signal`'s container height in styles.css by hand. Both
   now use `cqh` (container query height, 1cqh = 1% of `.wk-screen`'s own
   rendered height -- see the `container-type: size` note above `.wk-h`
   in styles.css) instead of a fixed px value, so the bars scale exactly
   with the box at any window size instead of needing hand re-tuning
   every time a real screenshot shows a different viewport. Formula tops
   out AT `.wk-signal`'s own 6.5cqh height. */
function SignalBars({ level }: { level: number }) {
  const bars = Math.max(1, Math.min(4, Math.round((level / 100) * 4)));
  return (
    <span className="wk-signal" aria-label={`${level}% signal`}>
      {[1, 2, 3, 4].map((b) => (
        <i key={b} className={b <= bars ? "on" : ""} style={{ height: `${1.5 + b * 1.25}cqh` }} />
      ))}
    </span>
  );
}

export function WifiKiosk({
  config,
  onChoose,
}: {
  config: WifiKioskConfig;
  onChoose: (net: WifiKioskNetwork) => void;
}) {
  const [stage, setStage] = useState<Stage>("off");
  const [active, setActive] = useState<WifiKioskNetwork | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipSeen, setTipSeen] = useState(false);
  const [card, setCard] = useState("");
  const [attempted, setAttempted] = useState<Set<string>>(() => new Set());
  const spoke = useRef(false);
  const { containerRef: fitContainerRef, contentRef: fitContentRef } = useFitScale<HTMLDivElement>([
    stage,
    active?.id,
    showHelp,
    config,
  ]);

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    void audio.prefetchSpeech(
      config.body ?? "Welcome to the free Wi-Fi terminal. Please choose the best plan for your trip.",
      {
        voice: config.voiceId ?? "nova",
        lang: config.language ?? "en-US",
        instructions: "Speak as a friendly female station assistant: warm, clear, and welcoming.",
      },
    );
    if (config.lenaSafe) void audio.prefetchSpeech(config.lenaSafe, { voice: "nova", lang: config.language ?? "en-US" });
    if (config.lenaRisky) void audio.prefetchSpeech(config.lenaRisky, { voice: "nova", lang: config.language ?? "en-US" });
    return () => audio.cancelSpeech();
  }, []);

  const wake = () => {
    audio.powerOn();
    setStage("list");
    if (!spoke.current) {
      spoke.current = true;
      void audio.speak(
        config.body ?? "Welcome to the free Wi-Fi terminal. Please choose the best plan for your trip.",
        {
          voice: config.voiceId ?? "nova",
          lang: config.language ?? "en-US",
          instructions: "Speak as a friendly female station assistant: warm, clear, and welcoming.",
        },
      );
    }
  };

  const connect = (n: WifiKioskNetwork) => {
    setActive(n);
    if (n.trusted) setStage("real");
    else if (n.trapKind === "creds") setStage("creds");
    else setStage("fake");
  };

  const backToList = () => {
    setStage("list");
    setActive(null);
    setCard("");
  };

  const connectAsGuest = () => {
    setStage("connected");
    if (config.lenaSafe) void audio.speak(config.lenaSafe, { voice: "nova", lang: config.language ?? "en-US" });
  };
  const finishSafe = (n: WifiKioskNetwork) => {
    onChoose(n);
  };
  const recoverRisky = (n: WifiKioskNetwork) => {
    if (config.lenaRisky) void audio.speak(config.lenaRisky, { voice: "nova", lang: config.language ?? "en-US" });
    setAttempted((current) => new Set(current).add(n.id));
    setShowHelp(true);
    setStage("list");
    setActive(null);
    setCard("");
  };

  // "What do I do here?" for each screen. Names the controls but never the
  // right network: picking it is the lesson.
  const tip =
    stage === "off"
      ? "Tap the screen to switch the Wi-Fi terminal on."
      : stage === "list"
      ? "Max needs Wi-Fi for the trip. Tap Connect next to a network. Several names look official, so read them carefully and pick the one that really belongs to the station. Not sure? Tap \u201cHow do I know which one is safe?\u201d. A wrong pick shows you what an attacker sees, and you can try again."
      : stage === "real"
      ? `This is the station's sign-in page. Click \u201c${config.realPortal.payLabel ?? "Continue as guest \u2014 connect"}\u201d to get online, or Back to networks to choose again.`
      : stage === "connected"
      ? "Max is online. Click Continue to the platform."
      : stage === "fake"
      ? "This page wants a card number before you can connect. Decide: Pay & connect, or \u201cThis looks wrong \u2014 disconnect\u201d."
      : stage === "creds"
      ? "This page wants your work email and password just to get on Wi-Fi. Decide: Sign in, or \u201cThis looks wrong \u2014 disconnect\u201d."
      : "The browser warns that this connection is not safe. Click Disconnect and choose again.";

  return (
    <div className="wk-kiosk" aria-label="Free Wi-Fi terminal">
      <div className="wk-bezel">
        <div className="wk-topbar">
          <span className="wk-wifi-ico" aria-hidden>{"\u{1F4F6}"}</span>
          <span className="wk-title">{config.kioskName ?? "Free Wi-Fi · Station terminal"}</span>
          <span className="wk-clock">{stage === "off" ? "" : "09:14"}</span>
          <button
            type="button"
            className={`wk-tip-btn${tipSeen ? "" : " pulse"}${tipOpen ? " on" : ""}`}
            aria-label="Tip: what to do here"
            aria-expanded={tipOpen}
            onClick={() => {
              setTipOpen((v) => !v);
              setTipSeen(true);
            }}
          >
            <span aria-hidden>{"\u{1F4A1}"}</span> Tip
          </button>
        </div>
        {tipOpen && (
          <div className="wk-tip" role="note">
            <div className="wk-tip-h">What to do here</div>
            <p>{tip}</p>
            <button type="button" className="wk-tip-close" onClick={() => setTipOpen(false)}>
              Got it
            </button>
          </div>
        )}

        {stage === "off" && (
          <button type="button" className="wk-wake" onClick={wake}>
            <span className="wk-wake-ico" aria-hidden>{"\u{1F4F6}"}</span>
            <span className="wk-wake-title">Free Station Wi-Fi</span>
            <span className="wk-wake-hint">Tap the screen to get online</span>
          </button>
        )}

        {stage === "list" && (
          <div className="wk-screen" ref={fitContainerRef}>
            <div className="wk-fit" ref={fitContentRef}>
              <h2 className="wk-h">{config.headline ?? "Available networks"}</h2>
              <p className="wk-sub">{config.body ?? "Tap a network to connect."}</p>
              <ul className="wk-nets">
                {config.networks.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={`wk-net ${attempted.has(n.id) ? "attempted" : ""}`}
                      disabled={attempted.has(n.id)}
                      onClick={() => connect(n)}
                    >
                      <span className={`wk-lock ${n.encrypted ? "locked" : "open"}`} aria-hidden>
                        {n.encrypted ? "\u{1F512}" : "\u{1F513}"}
                      </span>
                      <span className="wk-net-info">
                        <span className="wk-ssid-name">{n.ssid}</span>
                        <span className="wk-net-meta">
                          <small>{n.encrypted ? "Secured" : "Open"}</small>
                          <SignalBars level={n.signal} />
                        </span>
                      </span>
                      <span className="wk-connect">{attempted.has(n.id) ? "Avoid" : "Connect"}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" className="wk-help-toggle" onClick={() => setShowHelp((v) => !v)}>
                {showHelp ? "Hide" : "How do I know which one is safe?"}
              </button>
              {showHelp && config.verifyTips && config.verifyTips.length > 0 && (
                <ul className="wk-help">
                  {config.verifyTips.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {stage === "real" && active && (
          <div className="wk-screen wk-browser" ref={fitContainerRef}>
            <div className="wk-fit" ref={fitContentRef}>
              <div className="wk-urlbar ok">
                <span className="wk-url-lock" aria-hidden>{"\u{1F512}"}</span>
                <span className="wk-url">{config.realPortal.url}</span>
              </div>
              <div className="wk-page">
                <div className="wk-brand ok">{config.realPortal.brand}</div>
                <h3>{config.realPortal.headline}</h3>
                {config.realPortal.body && <p>{config.realPortal.body}</p>}
                <div className="wk-actions">
                  <button type="button" className="btn wk-pay" onClick={connectAsGuest}>
                    {config.realPortal.payLabel ?? "Continue as guest — connect"}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={backToList}>
                    Back to networks
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {stage === "connected" && active && (
          // Copy trimmed 2026-07-13 to fit the fixed kiosk screen box without
          // scrolling (see .wk-connected-list rule in styles.css) -- the
          // original 3 full paragraphs needed ~2.5x the available height.
          // Same 3 teaching points, one crisp line each.
          <div className="wk-screen wk-connected" ref={fitContainerRef}>
            <div className="wk-fit" ref={fitContentRef}>
              <div className="wk-connected-ico" aria-hidden>{"\u2705"}</div>
              <h3>Connected as a guest — the right pick</h3>
              <ul className="wk-connected-list">
                <li>
                  <strong>Real operator network</strong> — strongest signal, no login page.
                </li>
                <li>
                  <strong>HTTPS still protects you</strong> even on an open network.
                </li>
                <li>
                  <strong>No credentials asked</strong> — a fake portal always asks for one.
                </li>
              </ul>
              <button type="button" className="btn" onClick={() => finishSafe(active)}>
                Continue to the platform
              </button>
              <p className="wk-connected-hint">Click Continue to connect and carry on with the course.</p>
            </div>
          </div>
        )}

        {stage === "fake" && active && (
          <div className="wk-screen wk-browser" ref={fitContainerRef}>
            <div className="wk-fit" ref={fitContentRef}>
              <div className="wk-urlbar bad">
                <span className="wk-url-lock warn" aria-hidden>{"\u26A0"}</span>
                <span className="wk-url">{config.fakePortal.url}</span>
                <span className="wk-url-tag">Not secure</span>
              </div>
              <div className="wk-page">
                <div className="wk-brand">{config.fakePortal.brand}</div>
                <h3>{config.fakePortal.headline}</h3>
                <div className="wk-card-form">
                  <label>
                    <span>{config.fakePortal.cardLabel ?? "Card number (required to connect)"}</span>
                    <input
                      value={card}
                      onChange={(e) => setCard(e.target.value)}
                      inputMode="numeric"
                      placeholder="•••• •••• •••• ••••"
                    />
                  </label>
                </div>
                <div className="wk-actions">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => setStage("cert")}
                  >
                    Pay &amp; connect
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={backToList}>
                    This looks wrong — disconnect
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {stage === "creds" && active && (
          <div className="wk-screen wk-browser" ref={fitContainerRef}>
            <div className="wk-fit" ref={fitContentRef}>
              <div className="wk-urlbar bad">
                <span className="wk-url-lock warn" aria-hidden>{"\u26A0"}</span>
                <span className="wk-url">login.{active.ssid.toLowerCase().replace(/[^a-z0-9]/g, "")}.portal-auth.net</span>
                <span className="wk-url-tag">Not secure</span>
              </div>
              <div className="wk-page">
                <div className="wk-brand">Sign in to Wi-Fi</div>
                <h3>Enter your work email and password to continue</h3>
                <div className="wk-card-form">
                  <label><span>Work email</span><input placeholder="max.berger@company.com" /></label>
                  <label><span>Password</span><input type="text" className="cf-mask" placeholder="••••••••••" autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-form-type="other" /></label>
                </div>
                <div className="wk-actions">
                  <button type="button" className="btn btn-danger" onClick={() => setStage("cert")}>
                    Sign in
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={backToList}>
                    This looks wrong — disconnect
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {stage === "cert" && active && (
          <div className="wk-screen wk-browser" ref={fitContainerRef}>
            <div className="wk-fit" ref={fitContentRef}>
              <div className="wk-urlbar bad">
                <span className="wk-url-lock warn" aria-hidden>{"\u26A0"}</span>
                <span className="wk-url">sbb.ch/tickets</span>
              </div>
              <div className="wk-certerr">
                <div className="wk-certerr-ico" aria-hidden>{"\u26A0"}</div>
                <h3>{config.certError.title}</h3>
                <p>{config.certError.body}</p>
                {config.certError.errorCode && <div className="wk-certerr-code">{config.certError.errorCode}</div>}
                <button type="button" className="btn wk-certerr-btn" onClick={() => recoverRisky(active)}>
                  Disconnect and choose again
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
